/**
 * Comparing two versions of an article.
 *
 * A line diff rather than a word one: article bodies are Markdown, where a line
 * is a paragraph, a bullet or a heading — the unit someone actually edits. Pure
 * and separate from the screen so it can be tested as arithmetic.
 */

/** `created_by` on anything the merge automation published. */
export const AUTOMATION_AUTHOR = "helpdesk-automation"

export interface DiffLine {
  kind: "added" | "removed"
  text: string
}

/**
 * The lines that differ between two bodies, in reading order.
 *
 * Unchanged lines are left out: the question is what this version did, and on a
 * page-length body the answer drowns in context that did not move.
 */
export function diffLines(before: string, after: string): DiffLine[] {
  const from = before ? before.split("\n") : []
  const to = after ? after.split("\n") : []

  // Longest common subsequence, so a paragraph inserted in the middle does not
  // report everything after it as changed.
  const common: number[][] = Array.from({ length: from.length + 1 }, () =>
    new Array<number>(to.length + 1).fill(0),
  )
  for (let i = from.length - 1; i >= 0; i--) {
    for (let j = to.length - 1; j >= 0; j--) {
      common[i][j] =
        from[i] === to[j]
          ? common[i + 1][j + 1] + 1
          : Math.max(common[i + 1][j], common[i][j + 1])
    }
  }

  const changes: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < from.length && j < to.length) {
    if (from[i] === to[j]) {
      i++
      j++
    } else if (common[i + 1][j] >= common[i][j + 1]) {
      changes.push({ kind: "removed", text: from[i++] })
    } else {
      changes.push({ kind: "added", text: to[j++] })
    }
  }
  while (i < from.length) changes.push({ kind: "removed", text: from[i++] })
  while (j < to.length) changes.push({ kind: "added", text: to[j++] })

  // A body that only gained or lost blank lines is not a change worth showing.
  return changes.some((change) => change.text.trim()) ? changes : []
}
